import React, { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { Layout } from '../../template/layout'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@/app/store'
import { updateCheckInData } from '@/features/checkIn/check-in-slice'
import {
  SearchReservationInput,
  SearchReservationParams,
  SearchType,
  clearErrorMessage,
  schema,
  searchCheckIn,
} from '@/features/checkIn/search-reservations-slice'

import { MessageType, toastAction } from '@/features/notification/toast-slice'
import { useQuerySearchParams } from '@/hooks/use-query-search-params'
import { useHotelId } from '@/hooks/use-hotel-id'
import { useAppSelector } from '@/app/hooks'
import { Search } from '@/components/organisms/reservation/search'
import { SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckInStep } from '@/components/organisms/checkIn/check-in-step'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/react'

export const ReservationSearch: React.FC = () => {
  const errorMessage = useAppSelector(state => state.checkIn.searchCheckIn.errorMessage)
  const { t } = useTranslation()
  const { hotelId } = useQuerySearchParams<{ hotelId: string }>()
  const { getHotelId } = useHotelId()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const useFormReturn = useForm<SearchReservationInput>({
    resolver: zodResolver(schema),
  })

  const onSearch: SubmitHandler<SearchReservationInput> = async submitData => {
    const clearSelectedReservationParam = {
      reservationIds: [],
      selectedReservations: [],
    }
    if (submitData.searchType === SearchType.Phone) {
      dispatch(updateCheckInData({ ...clearSelectedReservationParam, telephone: submitData.reservationTel }))
    } else if (submitData.searchType === SearchType.ReservationId) {
      dispatch(updateCheckInData({ ...clearSelectedReservationParam, reservationId: submitData.reservationId }))
    } else if (submitData.searchType === SearchType.GuestName) {
      dispatch(updateCheckInData({
        ...clearSelectedReservationParam,
        reservationGivenName: submitData.reservationGivenName,
        reservationFamilyName: submitData.reservationFamilyName,
      }))
    }

    const params: SearchReservationParams = {
      hotelId: getHotelId() || hotelId,
      reservationTel: submitData.reservationTel,
      reservationId: submitData.reservationId,
      reservationGivenName: submitData.reservationGivenName,
      reservationFamilyName: submitData.reservationFamilyName,
    }
    const resultAction = await dispatch(searchCheckIn(params))

    if (resultAction.meta.requestStatus === 'fulfilled') {
      return navigate(`/checkin-reservation-confirm`)
    }

    if (searchCheckIn.rejected.match(resultAction)) {
      if (resultAction.payload?.errorMessage) {
        dispatch(toastAction.setMessage(MessageType.Error, t(resultAction.payload.errorMessage)))
        return
      }
    }
  }

  useEffect(() => {
    if (errorMessage) {
      dispatch(toastAction.setMessage(MessageType.Error, t(errorMessage)))
    }
    dispatch(clearErrorMessage())
  }, [])

  return (
    <Layout>
      <CheckInStep current={0} />
      <p css={leadTextStyle}>{t('Please enter your reservation information')}</p>
      <Search onSearch={onSearch} useFormReturn={useFormReturn} />
    </Layout>
  )
}

const leadTextStyle = css({
  textAlign: 'center',
  fontSize: '14px',
  padding: '16px 0',
})
